import { useCallback, useEffect, useRef } from 'react';

type ScrollOffset = {
  /** Offset from top in pixels (positive = scroll less, element appears lower) */
  top?: number;
  /** Offset from bottom in pixels (for future use with block: 'end') */
  bottom?: number;
};

type UseScrollToElementOptions = {
  /** Offset from scroll position to account for fixed headers or spacing */
  offset?: ScrollOffset;
  /** Approximate scroll duration in ms, used to delay callback (default: 700) */
  scrollDuration?: number;
  /** Callback fired after scroll animation completes */
  onScrollComplete?: () => void;
};

const DEFAULT_SCROLL_DURATION = 700;

/**
 * Finds the nearest scrollable ancestor of an element.
 */
const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;

  while (parent) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
    const hasOverflow = parent.scrollHeight > parent.clientHeight;

    if (isScrollable && hasOverflow) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

/**
 * Hook to scroll an element into view with optional offset and callback.
 * Works with any scroll container (drawers, modals, etc.).
 *
 * @example
 * const { elementRef, scrollToElement } = useScrollToElement({
 *   offset: { top: 16 },
 *   onScrollComplete: () => setShouldAnimate(true),
 * });
 *
 * useEffect(() => {
 *   if (shouldScroll) scrollToElement();
 * }, [shouldScroll, scrollToElement]);
 *
 * return <div ref={elementRef}>...</div>;
 */
export const useScrollToElement = <T extends HTMLElement = HTMLDivElement>(
  options: UseScrollToElementOptions = {},
) => {
  const { offset, scrollDuration = DEFAULT_SCROLL_DURATION, onScrollComplete } = options;

  const elementRef = useRef<T>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToElement = useCallback(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;
    const offsetTop = offset?.top ?? 0;

    if (offsetTop === 0) {
      // No offset: use native scrollIntoView for simplicity
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      // Use manual scroll calculation when offset is needed
      const scrollableParent = findScrollableParent(element);

      if (scrollableParent) {
        const elementRect = element.getBoundingClientRect();
        const parentRect = scrollableParent.getBoundingClientRect();
        const relativeTop =
          elementRect.top - parentRect.top + scrollableParent.scrollTop - offsetTop;

        scrollableParent.scrollTo({
          top: Math.max(0, relativeTop),
          behavior: 'smooth',
        });
      } else {
        // Fallback: scroll window with offset
        const elementRect = element.getBoundingClientRect();
        const absoluteTop = elementRect.top + window.scrollY - offsetTop;

        window.scrollTo({
          top: Math.max(0, absoluteTop),
          behavior: 'smooth',
        });
      }
    }

    if (onScrollComplete) {
      // Clear any pending callback from rapid calls
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onScrollComplete();
      }, scrollDuration);
    }
  }, [offset?.top, scrollDuration, onScrollComplete]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { elementRef, scrollToElement };
};
